import React from 'react';
import styled from '@emotion/styled';
import * as size from '@constant/size';
import * as color from '@constant/color';
import ImageBlock from '@component/common/block/image-block';
import TextBlock from 'component/common/block/text-block';
import iyagigoLandingLogoImg from '@asset/image/iyagigo-landing-logo.webp';
import appStoreBadge from '@asset/image/app-store-badge.svg';
import googlePlayBadge from '@asset/image/google-play-badge.png';
import Gap from '@component/common/gap';
import { IS_IYAGIGO_MOBILE_VIEW, IS_IOS, IS_ANDROID } from '@config/environment';
import { useNavigate } from 'react-router-dom';
import { AUTH_PATH } from '@constant/auth/route-path';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { IYAGIGO_IOS_STORE_URL, IYAGIGO_ANDROID_STORE_URL } from '@constant/iyagigo/variable';

const Background = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: ${color.BROWN_4};
`;

const PageContainer = styled.div``;

const CopyWriteContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
`;

const WebBeginContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: flex-start;
`;

const MobileBeginContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
`;

const BeginTextContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  z-index: 10;
`;

const Footer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: 50px;
  background-color: ${color.BLACK_2};
  padding: 0 0 0 ${size.MARGIN_2};
`;

const TermLink = styled.a`
  font-size: small;
  color: ${color.GRAY_2};
  text-align: left;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const StoreBadgeContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const IS_FOOTER_VISIBLE = false;

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const handleBeginClick = () => {
    if (IS_IOS) {
      window.open(IYAGIGO_IOS_STORE_URL, '_blank');
    } else if (IS_ANDROID) {
      window.open(IYAGIGO_ANDROID_STORE_URL, '_blank');
    } else {
      navigate(IYAGIGO_PATH.INITIAL_SERVICE_PATH);
    }
  };
  const handleIosStoreClick = () => window.open(IYAGIGO_IOS_STORE_URL, '_blank');
  const handleAndroidStoreClick = () => window.open(IYAGIGO_ANDROID_STORE_URL, '_blank');

  return (
    <>
      {IS_IYAGIGO_MOBILE_VIEW ? (
        <Background>
          <PageContainer>
            <Gap height={size.MARGIN_3} />
            <CopyWriteContainer>
              <TextBlock text={'이야기고(IYAGIGO)는'} design={{ text: { size: 'big', bold: true } }} />
              <Gap height={size.MARGIN_1} />
              <TextBlock text={'이야기와 言語(GO)를 더한 이름이에요.'} design={{ text: { size: 'big', bold: true } }} />
              <Gap height={size.MARGIN_1} />
              <TextBlock text={'매일 10분 언어를 공부하면서,'} design={{ text: { size: 'large' } }} />
              <TextBlock text={'이야기하는 법을 배우는 곳입니다.'} design={{ text: { size: 'large' } }} />
            </CopyWriteContainer>
            <Gap height={`calc(5*${size.MARGIN_1})`} />
            <MobileBeginContainer>
              <TextBlock text={'간편하게 시작하는 일본어·영어 학습'} design={{ text: { size: 'big', bold: true } }} />
              <Gap height={size.MARGIN_1} />
              <TextBlock
                text={"매일 10분으로 '어휘, 문법, 표현' 꾸준히 공부하기"}
                design={{ text: { size: 'large' } }}
              />
              <Gap height={size.MARGIN_3} />
              <ImageBlock
                src={iyagigoLandingLogoImg}
                alt='back'
                design={{
                  image: { height: '250px' },
                  box: { width: '100%', margin: '0 auto 0 auto' },
                }}
              />
              <Gap height={size.MARGIN_3} />
              <TextBlock
                text='앱으로 시작하기'
                design={{
                  box: {
                    height: '50px',
                    backgroundColor: color.BROWN_1,
                    borderRadius: size.BORDER_RADIUS_2,
                    event: { cursor: 'pointer' },
                    margin: '0 auto 0 auto',
                    width: `calc(100% - 0px)`,
                  },
                  text: { size: 'large', color: color.BROWN_4 },
                }}
                click={{ handler: handleBeginClick }}
              />
            </MobileBeginContainer>
          </PageContainer>
        </Background>
      ) : (
        <Background>
          <PageContainer>
            <Gap height={size.MARGIN_5} />
            <CopyWriteContainer>
              <TextBlock text={'이야기고(IYAGIGO)는'} design={{ text: { size: 'giant', bold: true } }} />
              <Gap height={size.MARGIN_1} />
              <TextBlock
                // text={'이야기와 言語(ご)를 더한 이름이에요.'}
                text={'이야기와 言語(GO)를 더한 이름이에요.'}
                design={{ text: { size: 'giant', bold: true } }}
              />
              <Gap height={size.MARGIN_2} />
              <TextBlock
                // text={'매일 10분 언어를 공부하면서, 이야기하는 법을 배우는 곳입니다.'}
                text={'언어를 공부하면서, 이야기하는 법을 배우는 곳입니다.'}
                design={{ text: { size: 'big' } }}
              />
            </CopyWriteContainer>
            <Gap height={`calc(8*${size.MARGIN_1})`} />
            <WebBeginContainer>
              <BeginTextContainer>
                {/*<TextBlock text={'AI와 함께하는 JLPT 학습'} design={{ text: { size: 'huge', bold: true } }} />*/}
                <TextBlock
                  text={'간편하게 시작하는 일본어·영어 학습'}
                  design={{ text: { size: 'huge', bold: true } }}
                />
                <Gap height={size.MARGIN_1} />
                {/*<TextBlock text={'하루에 10분 AI 문제로 JLPT 공부하기'} design={{ text: { size: 'big' } }} />*/}
                {/*<TextBlock text={'딱 10분이면 돼요. 어휘, 문법, 표현까지'} design={{ text: { size: 'big' } }} />*/}
                <TextBlock
                  text={"매일 10분으로 '어휘, 문법, 표현' 꾸준히 공부하기"}
                  design={{ text: { size: 'big' } }}
                />
                <Gap height={size.MARGIN_3} />
                <TextBlock
                  text='지금 시작하기'
                  design={{
                    box: {
                      width: '350px',
                      height: '65px',
                      backgroundColor: color.BROWN_1,
                      borderRadius: size.BORDER_RADIUS_2,
                      event: { cursor: 'pointer' },
                      margin: '0 auto 0 auto',
                      // transform: `translate(0, -${size.MARGIN_1})`,
                    },
                    text: { size: 'big', color: color.BROWN_4 },
                  }}
                  click={{ handler: handleBeginClick }}
                />
                <Gap height={size.MARGIN_3} />
                <TextBlock
                  text={'앱으로도 이용할 수 있어요'}
                  design={{ text: { size: 'large', color: color.BROWN_1, bold: false } }}
                />
                <Gap height={size.MARGIN_1} />
                <StoreBadgeContainer>
                  <ImageBlock
                    src={appStoreBadge}
                    alt='App Store'
                    design={{ image: { height: '50px' }, box: { event: { cursor: 'pointer' } } }}
                    click={{ handler: handleIosStoreClick }}
                  />
                  <ImageBlock
                    src={googlePlayBadge}
                    alt='Google Play'
                    design={{ image: { height: '50px' }, box: { event: { cursor: 'pointer' } } }}
                    click={{ handler: handleAndroidStoreClick }}
                  />
                </StoreBadgeContainer>
              </BeginTextContainer>
              <ImageBlock
                src={iyagigoLandingLogoImg}
                alt='back'
                design={{
                  image: { height: IS_IYAGIGO_MOBILE_VIEW ? '200px' : '500px' },
                  box: {
                    // 버튼 가리는 현상 조심
                    transform: `translate(-100px, 50px)`,
                  },
                }}
              />
            </WebBeginContainer>
          </PageContainer>
        </Background>
      )}
      {IS_FOOTER_VISIBLE && (
        <Footer>
          <TermLink href={AUTH_PATH.SERVICE_TERM_PATH}>서비스이용약관</TermLink>
          <Gap width={'30px'} />
          <TermLink href={AUTH_PATH.PRIVATE_TERM_PATH}>개인정보처리방침</TermLink>
        </Footer>
      )}
    </>
  );
};

export default Landing;
